// Pure board-edge and height helpers (no Phaser), shared by the tile renderer, the hover chip and
// the gallery.

export type Dir = 'n' | 'e' | 's' | 'w';

/** The board edge a tile is closest to (ties: west, then east, then north) — the way an exit leads out. */
export function edgeDir(x: number, y: number, w: number, h: number): Dir {
  const d: Array<[number, Dir]> = [[x, 'w'], [w - 1 - x, 'e'], [y, 'n'], [h - 1 - y, 's']];
  let best = d[0]!;
  for (const c of d) if (c[0] < best[0]) best = c;
  return best[1];
}

/**
 * docs/66 RM13: the way an exit on a free-form field leads out — toward a side where the field
 * ends right beside it (off the rectangle or into `Void`), the nearest such; with none, the
 * rectangle's nearest edge. No side is assumed: heroes come in from anywhere.
 */
export function edgeDirOn(board: { w: number; h: number; tiles: ReadonlyArray<{ terrain: string } | undefined> }, x: number, y: number): Dir {
  const { w, h } = board;
  const off = (tx: number, ty: number): boolean => tx < 0 || ty < 0 || tx >= w || ty >= h || board.tiles[ty * w + tx]?.terrain === 'Void';
  const open: Array<[number, Dir]> = [];
  for (const [dx, dy, d, far] of [[-1, 0, 'w', x], [1, 0, 'e', w - 1 - x], [0, -1, 'n', y], [0, 1, 's', h - 1 - y]] as const) {
    if (off(x + dx, y + dy)) open.push([far, d]);
  }
  if (!open.length) return edgeDir(x, y, w, h);
  let best = open[0]!;
  for (const c of open) if (c[0] < best[0]) best = c;
  return best[1];
}

/** docs/66 RM15: how a square's height reads (water's −1 is level ground, never low ground). */
export const heightOf = (t: { elev: number } | undefined): number => Math.max(0, t?.elev ?? 0);

/** docs/66 RM15: the hover chip's words for high ground. */
export const HIGH_GROUND_WORDS = 'high ground: +1 to attacks on lower ground, +1 ranged reach';
